import { callApi, IApiErrorResponse } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import CommonTable, { ITableColumn } from '@/components/common/CommonTable';
import FilterComponent from '@/components/common/FilterComponent';
import MainHeading from '@/components/common/MainHeading';
import NavTabs from '@/components/common/NavTabs';
import TabContainer from '@/components/common/TabContainer';
import EditIcon from '@/components/Icons/EditIcon';
import PlusIcon from '@/components/Icons/PlusIcon';
import Layout from '@/components/Layout/Layout';
import { IPageInfo } from '@/interfaces/common.model';
import { setActiveSalaryHeadTab } from '@/redux/features/UserSlice';
import { RootState } from '@/redux/store';
import {
  getFilterUrl,
  orderByList,
  showToastNotification,
} from '@/utility/helper';
import useFilter from '@/hooks/useFilter';
import { RoutesLink } from '@/utility/RouteLinks';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ISalaryHead } from '@/interfaces/salary.model';
interface ITableData {
  id: string;
  index: number;
  name: string;
  type: string;
  isShowOfferLetter: string;
  isFromEmployeer: string;
  isBase?: boolean;
}
export interface ISalaryResponseData {
  salaryHead: ISalaryHead[];
  pageInfo: IPageInfo;
}
export interface ISalaryApiResponse {
  message: string;
  statuscode: number;
  data: ISalaryResponseData;
  status: boolean;
  underMaintainance: boolean;
}
interface IpageStates extends IPageInfo {
  pageSize: number;
  count: number;
  tableLoader: boolean;
  searchQuery: string;
  salaryHeadList: ITableData[];
  type: string;
}
interface IFilterArray {
  isShow: string;
  companyList: ITableData[];
}
const SalaryHead: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const activeTab = useSelector(
    (state: RootState) => state?.user?.activeSalaryHeadTab,
  );
  const filterDetails = useSelector(
    (state: RootState) => state?.user?.filtersStates,
  );
  const filterStatus = useSelector(
    (state: RootState) => state?.user?.filtersStates,
  );
  const tabs = [
    {
      tabId: 1,
      label: 'Earning',
      activeClass: 'btn px-5 rounded-start-2 rounded-end-0 active nav-tab-link',
      inactiveClass: 'btn px-5 rounded-start-2 rounded-end-0 nav-tab-link',
    },
    {
      tabId: 2,
      label: 'Deduction',
      activeClass: 'btn px-5 rounded-start-0 rounded-end-2 active nav-tab-link',
      inactiveClass: 'btn px-5 rounded-start-0 rounded-end-2 nav-tab-link',
    },
  ];
  const tableHeading: ITableColumn<ITableData>[] = [
    { value: 'index', label: '#' },
    { value: 'name', label: 'Name' },
    { value: 'type', label: 'Type' },
    { value: 'isShowOfferLetter', label: 'Show in Offer Letter' },
    { value: 'isFromEmployeer', label: 'From Employer' },
  ];
  const handleUpdate = (data: ITableData) => {
    if (data?.isBase) {
      showToastNotification(
        'info',
        "This is Default salary head can't be edited!",
      );
      return;
    } else {
      navigate(RoutesLink?.addSalaryHead, { state: { data: data } });
    }
  };
  const actionData = [
    {
      label: 'Edit',
      className: 'btn btn-link',
      icon: <EditIcon />,
      title: 'edit',
      clickHandler: handleUpdate,
    },
  ];
  const [pageStates, setPageStates] = useState<IpageStates>({
    currentPage: 1,
    pageSize: 10,
    count: 0,
    totalPages: 0,
    tableLoader: false,
    searchQuery: '',
    salaryHeadList: [],
    type: activeTab == 1 ? 'earning' : 'deduction',
  });
  const toggleTab = (tabId: number) => {
    dispatch(setActiveSalaryHeadTab(tabId));
    if (tabId == 1) {
      setPageStates((prev) => ({
        ...prev,
        type: 'earning',
        currentPage: 1,
      }));
    } else {
      setPageStates((prev) => ({
        ...prev,
        type: 'deduction',
        currentPage: 1,
      }));
    }
  };
  const getSalaryData = async () => {
    setPageStates((prev) => ({
      ...prev,
      tableLoader: true,
    }));
    const filter = {
      pageNumber: pageStates?.currentPage,
      orderBy: filterDetails?.orderBy,
      search: pageStates.searchQuery,
      pageSize: filterDetails?.pageSize,
      type: pageStates.type,
    };
    try {
      const response: ISalaryApiResponse = await callApi(
        API_URL + API_ENDPOINT.getSalaryHead + getFilterUrl(filter),
        {
          method: 'GET',
        },
      );
      if (response?.status) {
        const newData: ITableData[] = response?.data?.salaryHead?.map(
          (item, index) => ({
            ...item,
            index: index,
            isShowOfferLetter: item?.wantToShowInOfferLetter ? 'Yes' : 'No',
            isFromEmployeer: item?.fromEmployeer ? 'Yes' : 'No',
          }),
        );
        setPageStates((prev) => ({
          ...prev,
          salaryHeadList: newData,
          count: response?.data?.pageInfo?.total ?? 0,
          totalPages: response?.data?.pageInfo?.totalPages,
        }));
      }
    } catch (error) {
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong');
      }
    } finally {
      setPageStates((prev) => ({
        ...prev,
        tableLoader: false,
      }));
    }
    setPageStates((prev) => ({
      ...prev,
      tableLoader: false,
    }));
  };
  useEffect(() => {
    getSalaryData();
  }, [
    pageStates?.currentPage,
    pageStates?.type,
    pageStates?.searchQuery,
    pageStates.pageSize,
    filterStatus?.orderBy,
  ]);
  const {
    handleSearchChange,
    pageChangeHandler,
    orderByHandler,
    handleChangePageSize,
  } = useFilter(pageStates, setPageStates);
  return (
    <Layout>
      <div className="content">
        <MainHeading
          title={'Salary Head'}
          buttonText={'Add Salary Head'}
          buttonIcon={<PlusIcon />}
          buttonLink={RoutesLink.addSalaryHead}
        />
        <NavTabs tabs={tabs} onTabClick={toggleTab} activeTab={activeTab} />
        <TabContainer activeTab={activeTab} tabId={activeTab}>
          <FilterComponent<IFilterArray>
            handleSearch={handleSearchChange}
            orderByList={orderByList}
            isGrid={false}
            orderByHandler={orderByHandler}
          />
          <CommonTable<ITableData>
            tableHeading={tableHeading}
            tableData={pageStates?.salaryHeadList}
            ActionData={actionData}
            currentPage={pageStates?.currentPage}
            pageChangeHandler={pageChangeHandler}
            isLoading={pageStates?.tableLoader}
            totalPages={0}
            count={pageStates?.count}
            handleChangePageSize={handleChangePageSize}
            pageSize={pageStates.pageSize}
          />
        </TabContainer>
      </div>
    </Layout>
  );
};
export default SalaryHead;
